import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { LegalLinks, SUPPORT_EMAIL } from "../components/legal-links.tsx";

function Contact() {
  return <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;
}

function Section({ id, title, children }: { id?: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="mt-8 scroll-mt-6">
      <h2 className="heading mb-3 text-[23px] leading-tight">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function LegalPage({
  title,
  effective,
  children,
}: {
  title: string;
  effective?: string;
  children: ReactNode;
}) {
  return (
    // No household data here, so session replays may show its words.
    <main
      data-ph-unmask
      className="mx-auto min-h-dvh w-full max-w-[680px] px-6 pb-12"
      style={{ paddingTop: "calc(24px + env(safe-area-inset-top))" }}
    >
      <title>{`${title} · Babia`}</title>
      <header>
        <Link to="/" className="heading inline-flex min-h-11 items-center text-[26px]">
          ← Babia
        </Link>
        <div className="head-rule" />
        <h1 className="heading mt-8 text-[38px] leading-tight tracking-[-0.03em]">{title}</h1>
        {effective ? <p className="mt-3 text-[14px] opacity-65">Effective {effective}</p> : null}
      </header>
      <article className="mt-6 text-[16px] leading-relaxed [&_a]:underline [&_a]:underline-offset-4">
        {children}
      </article>
      <footer className="mt-10 border-t border-divider pt-4">
        <LegalLinks />
      </footer>
    </main>
  );
}

export function Support() {
  return (
    <LegalPage title="Support">
      <p>
        Babia is a shared newborn care log at babia.app, operated by Shiny Pebble Tech LLC. This
        page says how to reach us and where to find the answers parents ask for most.
      </p>
      <Section id="contact" title="Get help">
        <p>
          Email <Contact />. Write from the address you sign in with, so we can find your household.
          For a problem, say what you did, what happened, and what you expected. In Babia, you can
          also send us a message from Settings, under Feedback.
        </p>
      </Section>
      <Section id="assistants" title="AI assistants">
        <p>
          Each parent connects an assistant with their own Babia account, and Babia asks before
          anything is shared. To disconnect one, open Babia’s Settings and remove it under AI
          assistants. Our <Link to="/privacy">Privacy Policy</Link> lists what an assistant
          receives.
        </p>
      </Section>
      <Section id="plan" title="Trial and plan">
        <p>
          Section 7 of the{" "}
          <Link to="/terms" hash="payment">
            Terms of Service
          </Link>{" "}
          explains the free trial, what happens when it ends, and the price.
        </p>
      </Section>
      <Section id="data" title="Your data">
        <p>
          Your household’s log stays yours. To ask for a copy, or to delete your account or your
          household’s log, email <Contact /> from the address you sign in with. Section 6 of the{" "}
          <Link to="/privacy" hash="deletion">
            Privacy Policy
          </Link>{" "}
          explains how long we keep information.
        </p>
      </Section>
      <Section id="medical" title="Medical questions">
        <p>
          Babia is a care log, not medical advice. For a question about your baby’s health, ask your
          clinician. For an emergency, contact local emergency services.
        </p>
      </Section>
    </LegalPage>
  );
}

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" effective="November 2, 2026">
      <p>
        Babia is a shared newborn care log at babia.app, operated by Shiny Pebble Tech LLC (“we,”
        “us,” or “our”). This policy explains how we collect, use, and share information when you
        use Babia, including through an AI assistant you connect to it. For privacy questions or
        requests, contact <Contact />.
      </p>
      <Section title="1. Information we collect">
        <p>
          When you sign in with Google, we receive your name, email address, email verification
          status, profile picture, and Google account identifier. When you sign in with Facebook, we
          receive your name, email address, profile picture, and the identifier Facebook assigns to
          you for Babia. Our authentication system also stores sign-in tokens and session records,
          which can include your IP address, browser information, and sign-in and expiration times.
          We do not receive your Google or Facebook password. We also make a few test accounts for
          app directory reviewers, which sign in with an email address and a password. For those
          accounts we store only a hash of the password. You cannot make a password account.
        </p>
        <p>
          We store the information you enter: baby names, birth dates, and due dates, whether each
          baby is a girl or a boy (if you choose to say, used only to place weights on the growth
          curves), feeding amounts and labels, diaper changes, sleep times, medication names and
          doses, weights, notes, and their timestamps. We also store household membership, invite
          codes, preferences, and which account created an entry. Care logs may contain sensitive
          information; enter only what you want to keep and share.
        </p>
        <p>
          We process technical information, including request details, errors, diagnostic logs, and
          account and household identifiers, to operate and troubleshoot the service. If you contact
          us, we receive your email address and the contents of your message. If you connect an AI
          assistant, we also receive the information described in section 3.
        </p>
        <p>
          <strong>Feedback.</strong> When you send us feedback from the app or ask a connected
          assistant to send it, we receive your message, its kind (a problem, an idea, a question,
          praise, or general feedback when you pick none), and when you sent it. From the app we
          also receive the screen you sent it from and your browser’s name and version; from an
          assistant, which assistant sent it. We store it with your account and household, and send
          it with your name and email address to Sentry, where we read it and reply. We record that
          feedback was sent and its kind in usage analytics, never its words.
        </p>
        <p>
          <strong>Usage analytics.</strong> To see how Babia is used and where it breaks, we collect
          usage information with PostHog: the pages you view, where you tap or click, how long pages
          take to load, errors in the app, your browser, device, and screen size, which version of
          Babia it runs and when it updates, approximate location derived from your IP address, and
          recordings of how you move through the app. Recordings hide every word Babia shows inside
          the app and everything you type; they keep the layout of the screen, where you tap, and
          how you scroll and move between pages. The public homepage and these policy pages are
          recorded as they appear. Once you sign in, this information is linked to your name, email
          address, and household. We also record that a change was made to your household’s log,
          such as an entry added from the app or an assistant, a nap ended, or a baby added, with
          the kind of entry and when; these records do not include babies’ names or birth dates,
          amounts, medicine names or doses, or note text. We also record when someone in your
          household opens the checkout or completes a purchase, with which price applied.
        </p>
        <p>
          <strong>Bottle alerts.</strong> If you turn on bottle alerts on a device, your browser
          gives us an address at its push service (Google, Apple, Mozilla, or Microsoft, depending
          on the browser) and keys to encrypt messages to it. We store them with your account and
          household. Each alert is encrypted, so the push service that carries it cannot read it. An
          alert contains your babies’ names, how long it has been since a baby’s last logged bottle,
          and how far apart that baby’s bottles usually are, never an amount. It can show on your
          device’s lock screen. We record which bottle each alert was about. In usage analytics we
          record that an alert was sent, which check raised it (a bottle overdue, or one twin fed
          without the other), which push service carried it, how many minutes had passed since the
          last logged bottle, and whether the push service accepted it. We also record when you turn
          alerts on or off and when you tap an alert. These records never include an alert’s words.
        </p>
        <p>
          <strong>Payments.</strong> When someone in your household buys Babia, Polar (polar.sh)
          sells it to you as our merchant of record and processes the payment. We send Polar the
          buyer’s name, email address, and Babia account identifier, and your household’s
          identifier. Polar collects your card and billing details directly; we never receive your
          card number. From Polar we receive the order’s identifier, the product bought, whether it
          was paid or refunded, and when. We record which household paid, when, and at which price.
        </p>
      </Section>
      <Section title="2. How we use information">
        <p>
          We use this information to sign you in, save and display your logs (including where a
          weight falls on the World Health Organization’s growth standards), connect household
          members, remember preferences, send the bottle alerts you turn on, unlock Babia for a
          household that paid, provide support, protect accounts, diagnose problems, and understand
          which parts of Babia are used so we can improve them. Google access is limited to basic
          sign-in scopes (openid, email, and profile); Babia does not request access to Gmail
          messages, Google Drive files, contacts, or calendars. Facebook access is limited to the
          public_profile and email permissions; Babia does not request your friends, posts, photos,
          or Pages.
        </p>
        <p>
          We do not sell your personal information, use it for targeted advertising, or use Google
          or Facebook user data to train AI models. Our use and transfer of information received
          from Google APIs follow the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy">
            Google API Services User Data Policy
          </a>
          , including its Limited Use requirements.
        </p>
      </Section>
      <Section title="3. AI assistants">
        <p>
          You can connect Babia to an AI assistant that supports the Model Context Protocol (MCP),
          such as ChatGPT, Claude, or Gemini. Connecting is optional. Nothing is shared with an
          assistant until you sign in to Babia from it and allow it on Babia’s consent screen. After
          that, it can log, read, edit, move, and delete entries for your household, and send us
          your feedback, when you ask it to.
        </p>
        <p>
          <strong>What the assistant sends us.</strong> Each request it makes on your behalf
          contains only what that action needs: which baby, a time, and the entry’s details (a
          bottle amount with its unit and a formula name, a diaper kind, a medicine name and dose, a
          weight in pounds and ounces, kilograms, or grams, or a note’s kind and text), the ID of an
          entry to change or remove, or which entries to read (their dates, and optionally their
          type or a note's kind). If you ask it to send us feedback, it sends your message and its
          kind (see section 1). We do not receive your conversation with the assistant beyond those
          fields. An assistant can add fields of its own to a request, such as ChatGPT’s coarse
          location and anonymous identifiers. Babia does not read, store, or record them. We also
          receive the assistant’s registration details, such as its name and the web addresses it
          returns you to, and the network address of the system sending each request. For an
          assistant on the web, that is usually the platform’s own servers; for one running on your
          computer, it is your own.
        </p>
        <p>
          <strong>What we send back.</strong> So it can answer you and confirm what it did, the
          assistant receives information from your household’s log: your babies’ names, nicknames,
          birth dates with their ages, due dates with their corrected ages, and whether each is a
          girl or a boy; each baby’s latest weight; entries from any dates it asks about (the last
          day unless it asks for others), with their times, bottle amounts, formula and medicine
          names, medicine doses, diaper kinds, nap times, weights with their percentiles on the WHO
          growth standards, and notes with their kinds (bath, weight, and so on) and text; your
          household’s time zone, the unit it logs bottles in and its usual bottle sizes, the unit it
          reads weights in, usual formula and medicine names, and the usual doses you set; and
          entries that look forgotten. It does not receive your or your partner’s name, email
          address, or Google or Facebook account details, or your household’s invite codes.
        </p>
        <p>
          <strong>The status card and the feed chart.</strong> With your recent entries, the
          assistant also receives a summary for each baby, made from the same log: the last bottle
          and diaper change, whether the baby is asleep, today’s totals, and whether a bottle looks
          due. When it asks how the babies have been eating, it receives each baby’s bottle totals
          for each day, up to the last two weeks. An assistant that shows apps in its chat (for
          example Claude or ChatGPT) can draw these as a small card and a chart, pages Babia sends
          with the answer. The card shows only its summary and the entries that look forgotten, and
          the chart only the daily totals. They load nothing from outside Babia, and they send
          nothing back to us or to anyone else.
        </p>
        <p>
          <strong>What we store.</strong> We store the assistant’s registration, your consent, and
          the sign-in tokens we issue to it. Access tokens expire after an hour and refresh tokens
          after 30 days. Entries an assistant creates or changes are saved under your account and
          shared with your household, like entries you tap in. We do not store your conversations or
          the assistant’s requests themselves; an error during a request may be recorded in our
          server logs and error monitoring (see section 6). For usage analytics we record each
          request’s type, which tool the assistant used, whether it worked or why it was refused,
          how long it took, the names of the details it sent (such as “baby” or “amount”, never
          their values), and which assistant made it. We do not ask the assistant for anything a
          tool does not need, such as which AI model it uses or which chat a request came from. When
          you allow, decline, or remove an assistant, we record that you did and which assistant it
          was. These records do not include your babies’ names, entry contents, or note text.
        </p>
        <p>
          <strong>What we do not do.</strong> Babia does not run or call an AI model. It sends your
          log to no AI provider other than the assistant you connect, and only when that assistant
          asks. We do not use assistant requests for advertising. What you say to the assistant, and
          how it stores your chats, is governed by that assistant’s own privacy policy (for example
          OpenAI’s for ChatGPT, Anthropic’s for Claude, or Google’s for Gemini), not this one.
        </p>
        <p>
          <strong>Disconnecting.</strong> You can remove an assistant at any time in Babia’s
          Settings, under AI assistants. This deletes your consent and the sign-in tokens we issued
          to that assistant, and it cannot read or change your log again unless you connect it and
          allow it again. Each parent removes their own assistants. Removing Babia from the
          assistant’s own settings (its apps or connectors) stops the assistant from calling Babia,
          but your consent and its tokens stay with us until you remove it in Settings. The
          assistant’s registration stays too: everyone who uses that assistant shares it, and it
          holds only what the assistant published about itself, such as its name and web addresses.
          For anything else, email <Contact />.
        </p>
      </Section>
      <Section title="4. Who can access information">
        <p>
          Members of your household can see your name, email address, baby profiles, and shared
          logs. They can add, change, or delete shared information. Anyone who redeems a valid
          household invite code can join, so share codes only with people you trust.
        </p>
        <p>
          We use service providers to run Babia: Google and Meta (Facebook) for sign-in, Railway for
          application hosting and database storage, Sentry for server error monitoring and
          diagnostics and to receive the feedback you send (as described in section 1) when enabled,
          PostHog for usage analytics, session recordings, and error tracking when enabled (as
          described in section 1), Polar for payments (as described in section 1), your browser’s
          push service to deliver the bottle alerts you turn on (it receives your device’s address
          and an encrypted message it cannot read), and email providers to handle support messages.
          These providers process information needed for their services. The site also loads Google
          Fonts, which sends your browser’s font requests and associated technical information to
          Google. An AI assistant you connect receives the information described in section 3.
        </p>
        <p>
          We may access information as needed for support, maintenance, and security. We may
          disclose information when legally required or necessary to protect users and the service.
          Providers may process information outside your country.
        </p>
      </Section>
      <Section title="5. Cookies and device storage">
        <p>
          We use cookies for sign-in and sessions. One of them remembers how a browser last signed
          in (Google, Facebook, or a test account’s password), so the sign-in screen can point to
          it. We use browser storage to remember night mode, the selected baby, and the version of
          Babia that the browser last ran. These support the app’s functions. Blocking or clearing
          them may sign you out or reset preferences. When you turn on bottle alerts, Babia installs
          a service worker in that browser, which shows the alerts and opens Babia when you tap one.
          When usage analytics are on, PostHog stores an identifier for your browser and its current
          visit in a cookie and in browser storage, so pages and recordings from one visit can be
          grouped. You can turn usage analytics and recordings off for a browser in Babia’s
          Settings; that choice is remembered on that browser, and changes you make from it, and
          errors they cause, are no longer recorded either. It does not cover sign-ins or requests
          an AI assistant makes for you. Babia does not use advertising cookies.
        </p>
      </Section>
      <Section id="deletion" title="6. Retention, deletion, and your choices">
        <p>
          We retain account information and care logs while your account is in use and as needed to
          provide the service. You can remove individual entries with Undo and remove baby profiles
          where the Settings screen offers that option; removing a baby deletes that baby’s entries
          from the active database. We keep your consent for an AI assistant and its token records
          while your account exists or until you remove the assistant in Settings (see section 3).
          An assistant’s registration, which everyone who uses that assistant shares, is kept while
          the assistant can still use Babia. Server logs and Sentry error reports, which can include
          request details, IP addresses, error messages, and account and household identifiers, are
          kept for up to 90 days. We keep feedback you send while your account exists, and its copy
          in Sentry for up to 90 days. Usage analytics in PostHog, including the error reports and
          change records described in section 1, are kept for up to one year, and session recordings
          for up to 30 days. You can ask us to delete your usage analytics sooner. We keep a
          household’s purchase record (when it paid, at which price, and Polar’s order identifier)
          while the household exists, and delete it with the household. Polar, as the merchant of
          record, keeps the payment records that tax and accounting rules require, under its own
          privacy policy. We keep a device’s alert address and keys until you turn its alerts off,
          sign out on that device, its push service reports it gone, or your account is deleted. We
          keep the database record of which bottles already sent an alert for 7 days. The usage
          analytics about alerts follow the one-year period above.
        </p>
        <p>
          To request access, correction, an export, or deletion of your account or household data,
          email <Contact /> from the address you use to sign in. We may verify your identity and
          authority over shared household data before acting. Account deletion and deletion of
          shared household logs are separate requests. Some records may remain in backups until they
          expire or be retained where needed for legal or security obligations.
        </p>
        <p>
          You can revoke Babia’s Google connection in your{" "}
          <a href="https://myaccount.google.com/connections">Google Account settings</a>, and its
          Facebook connection in Facebook under Settings and privacy, then Settings, then Apps and
          websites. Revoking access does not itself delete data already stored in Babia; to delete
          it, email us as described above.
        </p>
        <p>
          Depending on where you live (for example California, Colorado, Connecticut, Texas, or
          Virginia), access, correction, deletion, and opting out of certain processing may be legal
          rights, and you may be able to complain to your local data protection authority. We will
          respond within the time required by applicable law and will not discriminate against you
          for exercising these rights.
        </p>
      </Section>
      <Section title="7. Security">
        <p>
          We use HTTPS on the production site, sign-in controls, and household access checks to
          protect information. No system is completely secure. Keep your Google or Facebook account
          and devices secure, and contact us if you suspect unauthorized access.
        </p>
      </Section>
      <Section title="8. Children’s information">
        <p>
          Babia is for adult parents and authorized caregivers, not for children to create accounts.
          Adults may enter information about children in their care and are responsible for having
          permission to do so. Contact us to request removal of information submitted without
          appropriate permission.
        </p>
      </Section>
      <Section title="9. Changes and contact">
        <p>
          We may update this policy as the service changes, including whenever what an AI assistant
          can send to or receive from Babia changes, and will update the effective date above. We
          will provide additional notice of material changes where required. Contact Shiny Pebble
          Tech LLC at <Contact /> with any privacy questions.
        </p>
      </Section>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage title="Terms of Service" effective="October 18, 2026">
      <p>
        These terms govern your use of Babia at babia.app, provided by Shiny Pebble Tech LLC (“we,”
        “us,” or “our”). By creating an account or using Babia, including through an AI assistant,
        you agree to these terms. If you do not agree, please do not use the service. Questions can
        be sent to <Contact />.
      </p>
      <Section title="1. Who can use Babia">
        <p>
          You must be at least 18 and an authorized parent or caregiver to use Babia. Use your own
          Google or Facebook account, keep it secure, and enter or share information only when you
          have permission. You are responsible for activity through your account and for whom you
          invite into your household.
        </p>
      </Section>
      <Section title="2. A care log, not medical advice">
        <p>
          Babia helps you record bottles, diaper changes, sleep, medications, weights, and notes. It
          does not provide medical advice, diagnoses, treatment, dosing recommendations, or
          emergency monitoring. Preset medication names, the usual doses you set, and bottle amounts
          are logging conveniences, not instructions for care. Always follow your clinician’s advice
          and verify entries before relying on them. For an emergency, contact local emergency
          services.
        </p>
        <p>
          A weight’s percentile compares a logged weight with the World Health Organization’s Child
          Growth Standards for a baby’s sex and age, counted from the due date for a baby born more
          than three weeks early. It describes the weight. It is not a diagnosis, and Babia does not
          flag or judge it. Your pediatrician reads a baby’s growth over time. The growth curves are
          from the WHO Child Growth Standards (World Health Organization, 2006).
        </p>
      </Section>
      <Section title="3. Your information and shared households">
        <p>
          You retain your rights to the information you enter. You give Babia permission to store,
          process, and display it as needed to operate the service and share it with your household.
          Our <Link to="/privacy">Privacy Policy</Link> explains how we handle it.
        </p>
        <p>
          Household members can view and modify shared logs and settings, including deleting data.
          Share invite codes carefully. You are responsible for the accuracy of your entries and for
          keeping an independent record of information essential to care.
        </p>
      </Section>
      <Section title="4. AI assistants">
        <p>
          If you connect an AI assistant, it acts on your instructions and under your account. It
          can log, change, move, and delete entries for your household, and those changes reach
          everyone in it. Assistants can misunderstand a request, choose the wrong baby or time, or
          describe your log inaccurately, so check what an assistant logs and tells you before
          relying on it. You are responsible for what you ask an assistant to do.
        </p>
        <p>
          The assistant is a third-party service governed by its provider’s own terms. We do not
          control it and are not responsible for what it says or does. You can disconnect it at any
          time from the assistant’s settings.
        </p>
      </Section>
      <Section title="5. Acceptable use">
        <p>
          Do not use Babia unlawfully, upload information you are not entitled to share, attempt to
          access another household without permission, bypass security controls, interfere with the
          service, or use it to distribute malicious or abusive content.
        </p>
      </Section>
      <Section title="6. Availability and changes">
        <p>
          We may change, suspend, or discontinue features or the service. We do not guarantee
          uninterrupted availability, error-free records, or that a log entry will be saved when
          your connection fails. We may restrict access to address abuse, security concerns,
          violations of these terms, or legal requirements.
        </p>
      </Section>
      <Section id="payment" title="7. Free trial and payment">
        <p>
          Each household gets a 14-day free trial of every feature, starting when it adds its first
          baby. When the trial ends, the household’s log stays readable, but nobody in it can add,
          change, or remove entries until someone in the household buys Babia.
        </p>
        <p>
          The purchase is a single payment per household, not a subscription. It covers up to 10
          members of that household and does not move to another household. The price is shown
          before you pay, and the price shown when you start checkout is the price you pay. The
          founding price, $1.99, is offered until 100 households have paid it, so a checkout started
          just before the 100th purchase may still receive it. After that the price is $4.99. We may
          change the price for future purchases. Polar (polar.sh) sells Babia as our merchant of
          record and handles payment, taxes, and receipts under its own terms. Send refund requests
          to <Contact />. If a purchase is fully refunded, the household returns to the limits
          above. Households that used Babia before payments began keep every feature without paying.
        </p>
      </Section>
      <Section title="8. Ending your use">
        <p>
          You may stop using Babia at any time. Contact <Contact /> to request account or data
          deletion as described in the Privacy Policy. Signing out or revoking Google or Facebook
          access does not automatically delete your account or shared logs.
        </p>
      </Section>
      <Section title="9. Intellectual property">
        <p>
          Babia, including its software, design, and text, is owned by Shiny Pebble Tech LLC or its
          licensors and may not be copied or reused without permission. Your information remains
          yours, as described in section 3.
        </p>
      </Section>
      <Section title="10. Disclaimers and responsibility">
        <p>
          To the extent permitted by applicable law, Babia is provided “as is” and “as available,”
          without warranties of fitness for a particular purpose, merchantability, or
          non-infringement. To that same extent, we are not liable for indirect, incidental,
          special, or consequential losses arising from use of the service, including lost data or
          interruptions.
        </p>
        <p>
          These terms do not exclude or limit rights, warranties, or liability that cannot be
          excluded or limited under applicable law, including mandatory consumer protections.
        </p>
      </Section>
      <Section title="11. Governing law">
        <p>
          These terms are governed by the laws of the State of Texas, without regard to its
          conflict-of-laws principles. Any dispute arising out of or relating to these terms or your
          use of Babia will be brought exclusively in the state or federal courts located in Texas,
          and you consent to the personal jurisdiction of those courts. This does not remove
          protections that the law where you live gives you and that cannot be waived by agreement.
        </p>
      </Section>
      <Section title="12. Updates and contact">
        <p>
          We may revise these terms and will update the effective date above. We will provide notice
          of material changes where required. Continuing to use Babia after updated terms take
          effect means you accept them, subject to applicable law. Contact us at <Contact /> for
          support or questions about these terms.
        </p>
      </Section>
    </LegalPage>
  );
}
